import React from "react";
import { motion } from "framer-motion";
import { Eye, AlertTriangle, CheckCircle2 } from "lucide-react";

const BulkUpdateModal = ({
  onClose,
  onViewContracts,
  contractsUpdated,    // number
  skippedContracts,    // array
  errorFiles,          // array
}) => {
  const handleViewContracts = () => {
    onViewContracts();
  };

  const hasUpdated = contractsUpdated > 0;
  const hasSkipped = skippedContracts && skippedContracts.length > 0;
  const hasErrors = errorFiles && errorFiles.length > 0;

  const outcomeCount = [hasUpdated, hasSkipped, hasErrors].filter(Boolean).length;
  const isMixed = outcomeCount > 1;

  const isPureSuccess = hasUpdated && !hasSkipped && !hasErrors;
  const isPureSkipped = !hasUpdated && hasSkipped && !hasErrors;
  const isPureError   = !hasUpdated && !hasSkipped && hasErrors;

  // Helpers for the big header icon
  const renderTopIcon = () => {
    // Success icon (check)
    if (isPureSuccess || hasUpdated) {
      return (
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#16a34a"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-fluid-icon-md h-fluid-icon-md"
        >
          <motion.path
            d="M20 6L9 17L4 12"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
        </motion.svg>
      );
    }

    // Pure error icon (big X)
    if (isPureError) {
      return (
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#dc2626"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-fluid-icon-md h-fluid-icon-md"
        >
          <motion.line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
          <motion.line
            x1="6"
            y1="18"
            x2="18"
            y2="6"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          />
        </motion.svg>
      );
    }

    // Pure skipped icon (eye-ish/info)
    if (isPureSkipped) {
      return (
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6b7280"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-fluid-icon-md h-fluid-icon-md"
        >
          {/* simple “eye” style icon */}
          <motion.path
            d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
          <motion.circle
            cx="12"
            cy="12"
            r="3"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 0.3 }}
          />
        </motion.svg>
      );
    }

    // Fallback info icon (mixed but no updates, etc.)
    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width="64"
        height="64"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#6b7280"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-fluid-icon-md h-fluid-icon-md"
      >
        <motion.circle
          cx="12"
          cy="12"
          r="10"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
        <motion.line
          x1="12"
          y1="16"
          x2="12"
          y2="12"
          initial={{ y2: 14 }}
          animate={{ y2: 12 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.3 }}
        />
        <motion.line
          x1="12"
          y1="8"
          x2="12.01"
          y2="8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.4 }}
        />
      </motion.svg>
    );
  };

  return (
    <motion.div
      className="fixed inset-0 z-[1000] bg-black bg-opacity-40 flex items-center justify-center px-fluid-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-fluid-4 space-y-fluid-4"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        {/* HEADER: big icon + title + summary */}
        <div className="flex flex-col items-center space-y-fluid-1.5">
          <div
            className={
              isPureSuccess || hasUpdated
                ? "bg-green-100 rounded-full p-fluid-3"
                : isPureError
                ? "bg-red-100 rounded-full p-fluid-3"
                : "bg-gray-100 rounded-full p-fluid-3"
            }
          >
            {renderTopIcon()}
          </div>

          {/* Title */}
          {hasUpdated ? (
            <h2 className="text-fluid-xl font-semibold text-center text-[#111827]">
              {contractsUpdated} rate
              {contractsUpdated !== 1 ? "s" : ""} updated successfully
            </h2>
          ) : isPureError ? (
            <h2 className="text-fluid-xl font-semibold text-center text-[#111827]">
              All rows had errors
            </h2>
          ) : isPureSkipped ? (
            <h2 className="text-fluid-xl font-semibold text-center text-[#111827]">
              No rates were updated
            </h2>
          ) : (
            <h2 className="text-fluid-xl font-semibold text-center text-[#111827]">
              No rates were updated
            </h2>
          )}

          {/* Summary lines with inline icons (only useful when mixed or detailed) */}
          <div className="text-fluid-base text-gray-600 text-center max-w-xl space-y-1">
            {/* PURE SUCCESS: reuse simple message like the original modal */}
            {isPureSuccess && (
              <p>All selected rates were updated successfully.</p>
            )}

            {/* MIXED OR NON-PURE: show breakdown with small icons */}
            {!isPureSuccess && (
              <>
                {hasUpdated && (
                  <p className="flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>
                      {contractsUpdated} rate
                      {contractsUpdated !== 1 ? "s" : ""} updated successfully.
                    </span>
                  </p>
                )}

                {hasSkipped && (
                  <p className="flex items-center justify-center gap-1">
                    <Eye className="w-4 h-4 text-gray-500" />
                    <span>
                      {skippedContracts.length} row
                      {skippedContracts.length !== 1 ? "s were" : " was"} skipped.
                    </span>
                  </p>
                )}

                {hasErrors && (
                  <p className="flex items-center justify-center gap-1">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span>
                      {errorFiles.length} row
                      {errorFiles.length !== 1 ? "s had" : " had"} errors. Please
                      review them in your file.
                    </span>
                  </p>
                )}

                {!hasUpdated && hasSkipped && !hasErrors && (
                  <p>We didn’t detect any changes in the uploaded file.</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* DETAILS SECTION (same style you had) */}
        <div className="space-y-fluid-3 max-h-64 overflow-y-auto">
          {hasUpdated && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">
                Updated rates
              </h3>
              <p className="text-xs text-gray-500">
                For details, re-export the contracts or check the rate view.
              </p>
            </div>
          )}

          {hasSkipped && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">
                Skipped rows
              </h3>
              <ul className="text-xs text-gray-600 list-disc pl-5 space-y-1">
                {skippedContracts.map((row, idx) => (
                  <li key={idx}>
                    Row {row.rowNumber}: {row.reason || "No changes detected"}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasErrors && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1 text-red-600">
                Rows with errors
              </h3>
              <ul className="text-xs text-red-600 list-disc pl-5 space-y-1">
                {errorFiles.map((err, idx) => (
                  <li key={idx}>
                    Row {err.rowNumber}: {err.errorMessage || err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* FOOTER – same style as your existing modal */}
        <div className="flex flex-col items-center gap-fluid-3">
          <div className="flex gap-fluid-2">
            <button
              onClick={handleViewContracts}
              className="px-fluid-button-x py-fluid-button-y bg-custom-bg-blue text-white hover:bg-[#1a2a61] text-fluid-sm rounded font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              View Rates
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BulkUpdateModal;
